import { parsePhoneNumberFromString } from 'libphonenumber-js'
import validator from '@rushrecommerce/util-validator'
import { isNil, omitBy } from 'lodash-es'

/**
 * @description Remove specified keys from an object
 * @param {Object} obj - The object to modify
 * @param {Array} keysToRemove - The keys to remove from the object
 * @returns {Object} - The modified object
 */
export function removeObjKeys(obj, keysToRemove = []) {
  keysToRemove.forEach(key => {
    delete obj[key]
  })
  return obj
}

/**
 * @description Remove all keys with null or undefined values from an object
 * @param {Object} obj - The object to clean
 * @returns {Object} - The cleaned object
 */
export function cleanObj(obj, options = {}) {
  const { removeEmptyStrings = false } = options
  if (removeEmptyStrings) {
    return omitBy(obj, value => isNil(value) || value === '')
  }
  return omitBy(obj, isNil)
}

/**
 * @description Clean an object and validate its keys against a list of valid keys
 * @param {Object} obj - The object to clean and validate
 * @param {Array} validKeys - The list of valid keys
 * @returns {Object} - The cleaned and validated object
 */
export function cleanAndValidateObj(obj, validKeys = [], options = {}) {
  const { removeEmptyStrings = false } = options
  const cleanedObj = cleanObj(obj, { removeEmptyStrings })
  const validatedObj = {}

  validKeys.forEach(key => {
    if (key in cleanedObj) {
      validatedObj[key] = cleanedObj[key]
    }
  })

  return validatedObj
}

export function validateQueryParams(params = {}, validKeys = []) {
  validKeys = [...validKeys, 'limit', 'offset', 'sortBy'] // always allow pagination params
  return cleanAndValidateObj(params, validKeys, { removeEmptyStrings: true })
}

export function isValidHttpsImageUrl(v) {
  return isValidUrl(v) && v.startsWith('https://') && /\.(jpg|jpeg|png|ico|svg)$/i.test(v)
}

export function isValidHttpsUrl(v) {
  return isValidUrl(v) && v.startsWith('https://')
}

export function isValidEmail(v) {
  if (!v || typeof v !== 'string') return false
  return validator.isValidEmail(v)
}

export function isValidZip(v) {
  return validator.isValidZip(v)
}

export function isValidPhone(v) {
  if (!v || typeof v !== 'string') return false
  const parsedPhoneNumber = parsePhoneNumberFromString(v, 'US')
  return parsedPhoneNumber && parsedPhoneNumber.isValid()
}

export function isValidOrderNumber(v) {
  return typeof v === 'string' && v.trim().length > 2
}

export function isValidUrl(v) {
  return /^(https?:\/\/)?(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)$/.test(v)
}

export function isValidAddress(v, options = {}) {
  const { includeName = false, includePhone = false } = options
  const { firstName, lastName, address1, city, state, zip, phone } = v || {}
  return Boolean(
    (!includeName || (firstName && lastName)) &&
    (!includePhone || phone) &&
    address1 &&
    city &&
    state &&
    validator.isValidZip(zip)
  )
}

export function isValidTaxId(value) {
  // Simple EIN format validation XX-XXXXXXX
  const einRegex = /^\d{2}-\d{7}$/
  return einRegex.test(value)
}

/**
 * Validates a 9-digit ABA routing number using the official checksum algorithm.
 *
 * The checksum is calculated as follows:
 *   - Multiply the 1st, 4th, and 7th digits by 3.
 *   - Multiply the 2nd, 5th, and 8th digits by 7.
 *   - Multiply the 3rd, 6th, and 9th digits by 1.
 *   - Sum all these products.
 *   - The routing number is valid if the total modulo 10 is 0.
 *
 * Formula:
 *   checksum = (3 × (d1 + d4 + d7) + 7 × (d2 + d5 + d8) + (d3 + d6 + d9)) % 10
 *
 * @param {string} routingNumber - The 9-digit routing number to validate.
 * @returns {boolean} True if the routing number is valid, false otherwise.
 */
export function isValidRoutingNumber(routingNumber) {
  // ABA routing number checksum validation
  const digits = routingNumber.split('').map(Number)
  const checksum =
    (3 * (digits[0] + digits[3] + digits[6]) + 7 * (digits[1] + digits[4] + digits[7]) + (digits[2] + digits[5] + digits[8])) % 10

  return /^\d{9}$/.test(routingNumber) && checksum === 0
}

/**
 * Validates a US bank account number.
 *
 * This function removes all non-digit characters from the input before validation.
 * After cleaning, the account number must be between 4 and 17 digits long (inclusive),
 * which is the typical range for US bank accounts.
 *
 * @param {string} accountNumber - The account number to validate.
 * @returns {boolean} True if the cleaned account number is between 4 and 17 digits, false otherwise.
 */
export function isValidAccountNumber(accountNumber) {
  // Remove any non-digit characters
  const cleanNumber = accountNumber.replace(/\D/g, '')

  // Check length (typically 4-17 digits for US bank accounts)
  return cleanNumber.length >= 4 && cleanNumber.length <= 17
}
